import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import desktopAkashaImage from '../../../assets/images/backoffice/guardianNodes/guardianNodesWideBanner.png';
import { AnimatedDashboard } from '../../../components/animation/AnimatedDashboard';
import { getUserTickets } from '../../../slices/auth-slice';
import { H2, H3, H4, P1, P2, P3 } from '../../../styles/FontStyles';
import {
  Box,
  Flexed,
  SectionSeparator,
  TabSelectButton,
  Tabs
} from '../../../styles/GlobalComponents';
import { colors } from '../../../styles/GlobalStyles';
import { Desktop, Mobile } from '../../Kyc/KycStyled';
import { getAkashaHubs, getGuardianNodes } from '../Marketplace.actions';

import { USDT, formatUSDNoDecimals } from '../../../helpers/helperFunctions';

import { MY_HUBS_AKASHA } from '../../../routes/routes';
import { Icon } from '../../../styles/Icons';
import { PurchasePackageAndCombo } from '../PurchasePackageAndCombo/PurchasePackageAndCombo';

import {
  AdvantagesGrid,
  AkashaGrid,
  GridItem,
  Ic1,
  Ic2,
  Ic3,
  Ic4,
  Ic5
} from './Akasha.styled';

import Spinner from '../../../components/common/Spinner';
import { Button } from '../../../components/common/Button/Button';
import { NodePackage } from './NodePackage';

export const AkashaSuperNodeButton = styled(Button)`
  color: ${({ theme }) => theme.colors.neutral900};
  background: #01f1c1;
  font-size: 18px;
  font-weight: bold;
  height: 50px;
  border-radius: 20px;
  margin-top: 56px;
`;

export const Akasha = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { loading, hubs } = useSelector((state) => state.marketplace);

  const firstHub = [hubs[0]];

  useEffect(() => {
    dispatch(getGuardianNodes());
    dispatch(getUserTickets());
  }, [dispatch]);

  const [selectedHub, setSelectedHub] = useState(hubs?.[0] || null);
  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState('5');

  const handleSeeDetails = (hub) => {
    setSelectedHub(hub);
  };

  const handleSelectPackage = (hub) => {
    setModal(true);
    handleSeeDetails(hub);
  };

  const renderAkashaBanner = () => {
    return (
      <>
        <Desktop>
          <section style={{ position: 'relative' }}>
            <img
              src={desktopAkashaImage}
              alt='trading'
              style={{ width: '100%' }}
            />
          </section>
        </Desktop>

        <Mobile>
          <section style={{ position: 'relative' }}>
            <img
              src={desktopAkashaImage}
              alt='trading'
              style={{ width: '100%' }}
            />
          </section>
        </Mobile>
      </>
    );
  };
  useEffect(() => {
    setSelectedHub(hubs?.[0]);
  }, [hubs]);

  if (loading) return <Spinner fullScreen={true} />;

  return (
    <AnimatedDashboard zoom key={'marketplace'}>
      {renderAkashaBanner()}

      <Box dark p='60px 80px'>
        <H3 color={colors.primary900} mb='22'>
          Own the Future of DeFi — Guardian Nodes Available Now!
        </H3>
        <P1 color={colors.neutral0}>
          The revolution has begun. The world’s most powerful Layer 0 blockchain
          is here and now is your chance to secure a Super Node. Become a
          foundational part of a next-gen decentralized ecosystem—engineered for
          massive scalability, seamless cross-chain connectivity, and real-world
          utility that’s redefining DeFi. <br />
          <br /> Don’t just watch the future unfold—<b>own a piece of it.</b>
        </P1>

        <Button
          mt='30'
          maxWidth='250px'
          maxWidthSM='100%'
          onClick={() => history.push(MY_HUBS_AKASHA)}>
          Manage my Nodes
        </Button>
      </Box>

      <Box dark p='60px 80px'>
        <Flexed between center>
          <H3 color={colors.primary900} mb='22'>
            Guardian Nodes Packages
          </H3>

          <Tabs>
            <TabSelectButton selected={tab === '5'} onClick={() => setTab('5')}>
              <div>5 years</div>
            </TabSelectButton>
            <TabSelectButton
              selected={tab === '2.5'}
              onClick={() => setTab('2.5')}>
              <div>2.5 years</div>
            </TabSelectButton>
          </Tabs>
        </Flexed>

        <AkashaGrid>
          {hubs
            .filter((hub) => hub.packageGroup === tab)
            .map((hub, index) => (
              <NodePackage
                key={index}
                hub={hub}
                selectedHub={selectedHub}
                handleSelectPackage={handleSelectPackage}
              />
            ))}
        </AkashaGrid>
      </Box>

      <Box dark p='60px 80px'>
        <H3 color={colors.primary900} mb='30'>
          Advantages of Becoming a Super Node Holder
        </H3>

        <AdvantagesGrid>
          {advantages.map(({ Icon, title, text }, i) => (
            <AdvantageComponent key={i} Ic={Icon} title={title} text={text} />
          ))}
        </AdvantagesGrid>
      </Box>
      <PurchasePackageAndCombo
        selectedPackage={selectedHub}
        modal={modal}
        setModal={setModal}
      />
    </AnimatedDashboard>
  );
};

const advantages = [
  {
    Icon: Ic1,
    title: 'Earn Daily Rewards',
    text: 'Generate passive income every day by participating in blockchain validation and ecosystem growth.'
  },
  {
    Icon: Ic2,
    title: 'Locked-In Power Position',
    text: ' Secure a fixed, long-term position in the network’s foundation with priority in future expansion and utilities.'
  },
  {
    Icon: Ic3,
    title: 'Limited Availability',
    text: 'Super Nodes are scarce and exclusive—once they’re sold out, access is closed to the public.'
  },
  {
    Icon: Ic4,
    title: ' Priority Access to Ecosystem Projects',
    text: 'Get early access to upcoming launches, token drops, and DeFi opportunities within the Layer 0 ecosystem.'
  },

  {
    Icon: Ic5,
    title: 'Cross-Chain Yield Potential',
    text: '  Tap into rewards across multiple blockchains as the Layer 0 platform bridges the DeFi universe.'
  }
];

const AdvantageComponent = ({ Ic, title, text }) => {
  return (
    <Flexed alignStart gap='12px'>
      <Icon>
        <Ic />
      </Icon>
      <div>
        <H4 color={colors.primary900} mb='4'>
          {title}
        </H4>
        <P2 color={colors.neutral0}>{text}</P2>
      </div>
    </Flexed>
  );
};
